import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';
import { ResourcePicker } from '@/components/ui/resource-picker';


export default function MenuBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [handle, setHandle] = useState('');
  const [items, setItems] = useState<any[]>([]);

  const { data: menu, isLoading } = useQuery({
    queryKey: ['store-menu', id],
    queryFn: async () => {
      const res: any = await apiClient.get(`/merchant/menus/${id}`);
      return res;
    },
  });

  useEffect(() => {
    if (menu) {
      setTitle(menu.title);
      setHandle(menu.handle);
      // Ensure all items have IDs for rendering
      setItems(menu.items?.map((item: any) => ({ ...item, id: item.id || crypto.randomUUID() })) || []);
    }
  }, [menu]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // 1. Update menu title/handle
      await apiClient.patch(`/merchant/menus/${id}`, { title, handle });
      // 2. Update items
      const orderedItems = items.map((item, index) => ({
        ...item,
        position: index,
        // if ID is a temp uuid we just generated on client, we can send it or undefined.
        // the server handles item replacement safely.
      }));
      await apiClient.put(`/merchant/menus/${id}/items`, { items: orderedItems });
    },
    onSuccess: () => {
      toast('Menu saved successfully');
      queryClient.invalidateQueries({ queryKey: ['store-menus'] });
      queryClient.invalidateQueries({ queryKey: ['store-menu', id] });
    },
    onError: () => {
      toast('Error saving menu');
    },
  });

  const handleAddItem = () => {
    setItems([...items, { id: crypto.randomUUID(), title: '', type: 'CUSTOM', url: '', position: items.length }]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    
    if (field === 'type') {
      newItems[index] = { 
        ...newItems[index], 
        type: value,
        pageId: null,
        productId: null,
        collectionId: null,
        url: ''
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    
    setItems(newItems);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    const temp = newItems[index];
    newItems[index] = newItems[index - 1];
    newItems[index - 1] = temp;
    setItems(newItems);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    const temp = newItems[index];
    newItems[index] = newItems[index + 1];
    newItems[index + 1] = temp;
    setItems(newItems);
  };

  if (isLoading) {
    return <div className="flex justify-center p-12">Loading menu...</div>;
  }

  return (
    <div className="flex-1 space-y-6 p-8 pt-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store/navigation')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">Edit Menu</h2>
        </div>
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {saveMutation.isPending ? 'Saving...' : 'Save Menu'}
        </Button>
      </div>

      <div className="space-y-6">
        <div className="rounded-md border p-6 bg-white space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="handle">Handle</Label>
              <Input
                id="handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rounded-md border p-6 bg-white space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Menu Items</h3>
            <Button variant="outline" size="sm" onClick={handleAddItem}>
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          </div>

          <div className="space-y-4">
            {!items.length ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-md">
                No items in this menu. Add one to get started.
              </div>
            ) : (
              items.map((item, index) => (
                <div key={item.id} className="flex gap-4 p-4 border rounded-md items-start bg-slate-50">
                  <div className="flex flex-col gap-1 mt-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                      ↑
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleMoveDown(index)} disabled={index === items.length - 1}>
                      ↓
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-12 gap-4 flex-1">
                    <div className="col-span-4 space-y-2">
                      <Label>Name</Label>
                      <Input
                        placeholder="e.g. Home"
                        value={item.title}
                        onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="col-span-3 space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={item.type}
                        onValueChange={(val) => handleItemChange(index, 'type', val)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CUSTOM">Custom Link</SelectItem>
                          <SelectItem value="PAGE">Page</SelectItem>
                          <SelectItem value="PRODUCT">Product</SelectItem>
                          <SelectItem value="COLLECTION">Collection</SelectItem>
                          <SelectItem value="EXTERNAL">External Link</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5 space-y-2">
                      <Label>Link Destination</Label>
                      <ResourcePicker
                        type={item.type}
                        value={
                          item.type === 'PAGE' ? item.pageId :
                          item.type === 'PRODUCT' ? item.productId :
                          item.type === 'COLLECTION' ? item.collectionId :
                          item.url
                        }
                        onChange={(val, field) => handleItemChange(index, field, val)}
                      />
                    </div>
                  </div>

                  <Button variant="ghost" size="icon" className="text-red-500 mt-6" onClick={() => handleRemoveItem(index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
